import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  FileText,
  Send,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Filter,
  Activity,
  Users,
  Target,
  Info,
  Truck,
  AlertTriangle,
  Calendar,
  UserPlus,
  Settings,
  LayoutDashboard,
  ArrowRight,
  Bell,
  Briefcase,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useDashboard, DashboardPeriod } from '../hooks/useDashboard';
import { useJobsDashboard, JobsDashboardPeriod } from '../hooks/useJobsDashboard';
import { useOverviewDashboard } from '../hooks/useOverviewDashboard';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { CustomDropdown } from '../components/ui/CustomDropdown';
import { JOB_STATUS_CLASSES, JOB_STATUS_LABEL } from '../types/admin';

// ── Constants ───────────────────────────────────────────────────────────────

type DashboardTab = 'overview' | 'sales' | 'operations';
type SalesTypeFilter = 'all' | 'leads' | 'quotes';

const PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const REASON_COLORS = [
  '#059669', '#820263', '#2563EB', '#D97706', '#D90368', '#9E8FA3', '#0891B2', '#7C3AED',
];

const SOURCE_COLORS = [
  '#820263', '#059669', '#2563EB', '#D97706', '#D90368', '#0891B2', '#7C3AED', '#9E8FA3',
  '#DC2626', '#16A34A', '#CA8A04', '#7C3AED',
];

const ASSIGNMENT_BADGE: Record<string, { label: string; className: string }> = {
  ready: { label: 'Ready', className: 'bg-emerald-50 text-emerald-700' },
  partial: { label: 'Partial', className: 'bg-amber-50 text-amber-700' },
  unassigned: { label: 'Unassigned', className: 'bg-rose/10 text-rose' },
};

const FUNNEL_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-blue-200 text-blue-800',
  'bg-plum/15 text-plum', 'bg-plum/25 text-plum',
  'bg-plum/40 text-plum', 'bg-emerald-100 text-emerald-700',
];
const FUNNEL_BAR_COLORS = [
  'bg-blue-200', 'bg-blue-300',
  'bg-plum/20', 'bg-plum/35',
  'bg-plum/50', 'bg-emerald-500',
];

const EVENT_TYPE_ICONS: Record<string, { icon: typeof FileText; color: string }> = {
  lead: { icon: UserPlus, color: 'text-blue-600' },
  quote: { icon: Send, color: 'text-plum' },
  won: { icon: CheckCircle2, color: 'text-emerald-600' },
  lost: { icon: XCircle, color: 'text-amber-600' },
};


// ── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

// ── Custom Tooltip ──────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, formatter }: {
  active?: boolean;
  payload?: { value: number; name: string; color: string }[];
  label?: string;
  formatter?: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-stone rounded-lg shadow-brand-md px-3 py-2 text-xs">
      {label && <p className="font-semibold text-midnight mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-slate">{entry.name}:</span>
          <span className="font-mono font-medium text-midnight">
            {formatter ? formatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function DashboardPage() {
  const { currentUser, adminOrg, activeOrg, hasActiveSubscription } = useUserProfile();
  const noSubscription = !hasActiveSubscription && !currentUser?.isPlatformAdmin;
  const jobsEnabled = activeOrg?.featureJobs !== false;
  const leadsEnabled = activeOrg?.featureLeadPipeline ?? false;
  const fleetEnabled = activeOrg?.featureFleetManagement ?? false;
  const dispatchEnabled = activeOrg?.featureDispatch !== false;
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [showFilters, setShowFilters] = useState(false);
  const [period, setPeriod] = useState<DashboardPeriod>('12m');
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const orgId = activeOrg?.orgId ?? null;

  // Reset user/mover filter when switching tabs
  const handleTabChange = (tab: DashboardTab) => {
    setActiveTab(tab);
    setSelectedUserId('');
  };

  // Access guard
  if (!adminOrg && !currentUser?.isPlatformAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <BarChart3 size={48} className="text-slate mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Access Denied</h2>
        <p className="text-small text-slate">
          The dashboard is only available to organization admins.
        </p>
      </div>
    );
  }

  const tabConfigs: { id: DashboardTab; label: string; icon: typeof TrendingUp }[] = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'sales', label: 'Sales', icon: TrendingUp },
    ...(jobsEnabled ? [{ id: 'operations' as const, label: 'Operations', icon: Settings }] : []),
  ];

  const periodFilters = activeTab !== 'overview' ? (
    <div className="flex flex-col gap-1">
      {PERIODS.map(p => (
        <button
          key={p.value}
          type="button"
          onClick={() => setPeriod(p.value)}
          className={`px-3 h-8 rounded-lg text-xs font-medium text-left transition-all duration-150
            ${period === p.value
              ? 'bg-plum text-white shadow-brand-sm'
              : 'bg-white border border-stone text-slate hover:border-plum/40 hover:text-midnight'
            }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  ) : null;

  const tabContent = activeTab === 'overview' ? (
    <OverviewDashboardContent orgId={orgId} noSubscription={noSubscription} leadsEnabled={leadsEnabled} jobsEnabled={jobsEnabled} onSwitchTab={setActiveTab} />
  ) : activeTab === 'sales' ? (
    <SalesDashboardContent orgId={orgId} period={period} selectedUserId={selectedUserId} onUserChange={setSelectedUserId} noSubscription={noSubscription} leadsEnabled={leadsEnabled} />
  ) : (
    <OperationsDashboardContent orgId={orgId} period={period} selectedMoverId={selectedUserId} onMoverChange={setSelectedUserId} noSubscription={noSubscription} fleetEnabled={fleetEnabled} dispatchEnabled={dispatchEnabled} />
  );

  return (
    <>
      {/* ── Mobile layout ──────────────────────────────────────────── */}
      <div className="md:hidden px-4 pt-4 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="font-display text-h1 text-midnight shrink-0">Dashboard</h1>
        </div>

        {/* Horizontal tab bar */}
        <div className="flex gap-1 bg-mist rounded-xl p-1 mb-4">
          {tabConfigs.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => handleTabChange(t.id)}
              className={`flex-1 flex items-center justify-center gap-1 h-9 rounded-lg text-[12px] font-semibold transition-all duration-150
                ${activeTab === t.id
                  ? 'bg-white text-midnight shadow-brand-sm'
                  : 'text-slate hover:text-midnight'
                }`}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Mobile period filters */}
        {activeTab !== 'overview' && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowFilters(f => !f)}
              className="flex items-center gap-1.5 px-2.5 h-9 rounded-lg border border-stone
                         text-[13px] font-medium text-midnight hover:border-plum/40 transition-colors"
            >
              <Filter size={14} />
              Filters
              <ChevronDown size={12} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            {showFilters && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {PERIODS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPeriod(p.value)}
                    className={`px-3 h-8 rounded-lg text-xs font-medium transition-all duration-150
                      ${period === p.value
                        ? 'bg-plum text-white shadow-brand-sm'
                        : 'bg-white border border-stone text-slate hover:border-plum/40 hover:text-midnight'
                      }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tabContent}
      </div>

      {/* ── Desktop layout (md+) ───────────────────────────────────── */}
      <div className="hidden md:block px-6 lg:px-8 pt-4 pb-8 max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-h1 text-midnight">Dashboard</h1>
        </div>

        <div className="flex gap-6 items-start">
          {/* Left sidebar: vertical tabs + period filters */}
          <aside className="w-52 shrink-0 sticky top-20">
            <nav className="flex flex-col gap-1">
              {tabConfigs.map(t => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleTabChange(t.id)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-left transition-all duration-150 ${
                      isActive
                        ? 'bg-plum text-white shadow-brand-sm'
                        : 'text-slate hover:bg-mist hover:text-midnight'
                    }`}
                  >
                    <t.icon size={16} />
                    {t.label}
                  </button>
                );
              })}
            </nav>

            {periodFilters && (
              <>
                <div className="my-4 border-t border-stone" />
                {periodFilters}
              </>
            )}
          </aside>

          {/* Right content area */}
          <div className="flex-1 min-w-0">
            {tabContent}
          </div>
        </div>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── OVERVIEW DASHBOARD ──────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function OverviewDashboardContent({ orgId, noSubscription, leadsEnabled, jobsEnabled, onSwitchTab }: {
  orgId: string | null;
  noSubscription: boolean;
  leadsEnabled: boolean;
  jobsEnabled: boolean;
  onSwitchTab: (tab: DashboardTab) => void;
}) {
  const navigate = useNavigate();
  const { data, loading, error } = useOverviewDashboard(orgId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <Activity size={48} className="text-rose mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Something went wrong</h2>
        <p className="text-small text-slate mb-4">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, revenueTrend, weekSchedule, actionItems } = data;

  const ACTION_ICONS: Record<string, { icon: typeof Bell; color: string }> = {
    overdue_followup: { icon: Bell, color: 'text-rose' },
    unassigned_job: { icon: AlertTriangle, color: 'text-amber-600' },
    stale_quote: { icon: Send, color: 'text-plum' },
  };

  // Build KPI cards based on feature flags
  const kpiCards: { label: string; value: string; icon: React.ReactNode; color: 'plum' | 'emerald'; tooltip: string }[] = [
    {
      label: 'Revenue Won',
      value: formatCurrency(kpis.revenueWon),
      icon: <DollarSign size={18} />,
      color: 'emerald',
      tooltip: 'Total won revenue this month from booked quotes.',
    },
    {
      label: 'Active Pipeline',
      value: String(kpis.activePipeline),
      icon: <Briefcase size={18} />,
      color: 'plum',
      tooltip: leadsEnabled
        ? 'Active leads (new + contacted) plus draft and sent quotes.'
        : 'Draft and sent quotes currently in the pipeline.',
    },
    {
      label: 'Pipeline Value',
      value: formatCurrency(kpis.pipelineValue),
      icon: <TrendingUp size={18} />,
      color: 'emerald',
      tooltip: 'Dollar value of everything currently in the sales funnel.',
    },
    {
      label: 'Win Rate',
      value: `${kpis.winRate.toFixed(0)}%`,
      icon: <Target size={18} />,
      color: 'plum',
      tooltip: 'Booked ÷ (Sent + Booked + Declined) — overall conversion.',
    },
  ];

  // Add jobs this week if jobs are enabled
  if (jobsEnabled) {
    kpiCards.splice(3, 0, {
      label: 'Jobs This Week',
      value: String(kpis.jobsThisWeek),
      icon: <Calendar size={18} />,
      color: 'plum',
      tooltip: 'Jobs with move dates this week.',
    });
  }

  // Add overdue follow-ups if leads are enabled and there are any
  if (leadsEnabled && kpis.overdueFollowUps > 0) {
    kpiCards.push({
      label: 'Overdue Follow-ups',
      value: String(kpis.overdueFollowUps),
      icon: <Bell size={18} />,
      color: 'plum',
      tooltip: 'Leads past their follow-up date — need attention now.',
    });
  }

  // Limit to 6 KPIs max
  const displayKpis = kpiCards.slice(0, 6);

  const sparklineData = revenueTrend.map(d => ({
    ...d,
    label: formatMonth(d.month),
  }));

  return (
    <>
      {/* KPI Cards */}
      <div className={`grid gap-3 mb-6 ${displayKpis.length <= 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
        {displayKpis.map(kpi => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            icon={kpi.icon}
            color={kpi.color}
            tooltip={kpi.tooltip}
          />
        ))}
      </div>

      {/* Revenue Trend (compact sparkline) */}
      {sparklineData.length > 0 && (
        <div className="mb-6">
          <ChartCard title="Revenue Trend" tooltip="Won revenue over the last 6 months.">
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={sparklineData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradOverviewEmerald" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis hide />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} formatter={formatCurrency} />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#059669"
                  strokeWidth={2}
                  fill="url(#gradOverviewEmerald)"
                  dot={{ r: 3, fill: '#059669', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Two-column: Schedule + Action Items */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* This Week's Schedule */}
        {jobsEnabled && (
          <ChartCard title="This Week's Schedule" tooltip="Jobs scheduled this week, sorted by move date.">
            {weekSchedule.length > 0 ? (
              <>
                <div className="divide-y divide-stone/60">
                  {weekSchedule.map(j => {
                    const statusClasses = JOB_STATUS_CLASSES[j.jobStatus] ?? 'bg-stone text-slate';
                    const statusLabel = JOB_STATUS_LABEL[j.jobStatus] ?? j.jobStatus;
                    return (
                      <button
                        key={j.id}
                        type="button"
                        onClick={noSubscription ? undefined : () => navigate(`/moves/${j.id}`)}
                        disabled={noSubscription}
                        className={`flex items-center gap-3 py-2.5 w-full text-left -mx-1 px-1
                                   rounded-lg transition-colors group
                                   ${noSubscription ? 'opacity-60 cursor-not-allowed' : 'hover:bg-mist/50'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium text-midnight truncate transition-colors ${noSubscription ? '' : 'group-hover:text-plum'}`}>
                            {j.customerCompanyName}
                          </p>
                          <p className="text-xs text-slate">
                            {formatDate(j.moveDate)}
                            {j.crewSize > 0 && <> &middot; {j.crewSize} crew</>}
                          </p>
                        </div>
                        <span className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${statusClasses}`}>
                          {statusLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => onSwitchTab('operations')}
                  className="flex items-center gap-1 text-xs font-medium text-plum hover:text-plum/80 mt-3 transition-colors"
                >
                  View all in Operations <ArrowRight size={12} />
                </button>
              </>
            ) : (
              <p className="text-[13px] text-slate text-center py-10">No jobs scheduled this week</p>
            )}
          </ChartCard>
        )}

        {/* Action Items */}
        <ChartCard title="Action Items" tooltip="Things that need your attention right now.">
          {actionItems.length > 0 ? (
            <div className="divide-y divide-stone/60">
              {actionItems.map((item, i) => {
                const { icon: Icon, color } = ACTION_ICONS[item.type] ?? ACTION_ICONS.stale_quote;
                return (
                  <button
                    key={`${item.id}-${item.type}-${i}`}
                    type="button"
                    onClick={noSubscription ? undefined : () => navigate(item.link)}
                    disabled={noSubscription}
                    className={`flex items-center gap-3 py-2.5 w-full text-left -mx-1 px-1
                               rounded-lg transition-colors group
                               ${noSubscription ? 'opacity-60 cursor-not-allowed' : 'hover:bg-mist/50'}`}
                  >
                    <div className={`shrink-0 ${color}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium text-midnight truncate transition-colors ${noSubscription ? '' : 'group-hover:text-plum'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-slate truncate">{item.description}</p>
                    </div>
                    <ArrowRight size={14} className="text-slate/40 shrink-0" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <CheckCircle2 size={32} className="text-emerald-400 mb-2" />
              <p className="text-sm font-medium text-midnight">All caught up</p>
              <p className="text-xs text-slate mt-0.5">No action items right now.</p>
            </div>
          )}
        </ChartCard>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── SALES DASHBOARD ──────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function SalesDashboardContent({ orgId, period, selectedUserId, onUserChange, noSubscription, leadsEnabled }: {
  orgId: string | null;
  period: DashboardPeriod;
  selectedUserId: string;
  onUserChange: (id: string) => void;
  noSubscription: boolean;
  leadsEnabled: boolean;
}) {
  const navigate = useNavigate();
  const [reasonView, setReasonView] = useState<'won' | 'lost'>('won');
  const [typeFilter, setTypeFilter] = useState<SalesTypeFilter>('all');

  const { data, loading, error } = useDashboard(
    orgId,
    period,
    selectedUserId || undefined,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <Activity size={48} className="text-rose mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Something went wrong</h2>
        <p className="text-small text-slate mb-4">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, revenueOverTime, salesFunnel, wonReasonDistribution, lostReasonDistribution, leadSourceDistribution, recentActivity, availableUsers } = data;

  const revenueChartData = revenueOverTime.map(d => ({
    ...d,
    label: formatMonth(d.month),
  }));

  // When leads are disabled, hide lead-specific elements
  const showLeadsKpi = leadsEnabled;
  const showLeadSources = leadsEnabled && leadSourceDistribution.length > 0;
  // Filter funnel stages when leads disabled: skip Lead, Contacted, Estimate Scheduled
  const displayFunnel = leadsEnabled
    ? salesFunnel
    : salesFunnel.filter(s => !['Lead', 'Contacted', 'Estimate Scheduled'].includes(s.stage));
  const displayFunnelMax = displayFunnel[0]?.count || 1;

  // Filter recent activity based on type filter
  const displayActivity = typeFilter === 'all'
    ? recentActivity
    : typeFilter === 'leads'
      ? recentActivity.filter(a => a.eventType === 'lead')
      : recentActivity.filter(a => a.eventType !== 'lead');

  // Determine if everything is empty
  const totalRecords = kpis.activeLeads + kpis.totalQuotes;

  return (
    <>
      {/* Filters row: estimator + type */}
      <div className="mb-5 flex items-center gap-2 flex-wrap">
        {availableUsers.length > 1 && (
          <CustomDropdown
            options={[
              { value: '', label: 'All estimators' },
              ...availableUsers.map(u => ({ value: u.uid, label: u.name || 'Unknown' })),
            ]}
            value={selectedUserId}
            onChange={onUserChange}
            fullWidth={false}
            minWidth="160px"
            placeholder="All estimators"
          />
        )}

        {leadsEnabled && (
          <div className="flex gap-1 bg-mist rounded-lg p-0.5">
            {([['all', 'All'], ['leads', 'Leads'], ['quotes', 'Quotes']] as const).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setTypeFilter(val)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150
                  ${typeFilter === val
                    ? 'bg-white text-midnight shadow-brand-sm'
                    : 'text-slate hover:text-midnight'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {showLeadsKpi ? (
          <KpiCard
            label="Active Leads"
            value={String(kpis.activeLeads)}
            icon={<UserPlus size={18} />}
            color="plum"
            tooltip="Leads in 'new' or 'contacted' status — your current pipeline."
          />
        ) : (
          <KpiCard
            label="Quotes Drafted"
            value={String(kpis.draftCount + kpis.sentCount + kpis.bookedCount + kpis.declinedCount)}
            icon={<FileText size={18} />}
            color="plum"
            tooltip="Total quotes created in the selected period (excluding cancelled)."
          />
        )}
        <KpiCard
          label="Quotes Sent"
          value={String(kpis.quotesSent)}
          icon={<Send size={18} />}
          color="emerald"
          tooltip="Quotes sent to clients in the selected period — proposals awaiting decision."
        />
        <KpiCard
          label="Revenue Won"
          value={formatCurrency(kpis.revenueWon)}
          icon={<DollarSign size={18} />}
          color="plum"
          tooltip="Sum of final cost estimates from booked quotes in the selected period."
        />
        <KpiCard
          label="Win Rate"
          value={`${kpis.winRate.toFixed(0)}%`}
          icon={<Target size={18} />}
          color="emerald"
          tooltip="Booked ÷ (Sent + Booked + Declined) — your conversion rate."
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Sales Pipeline Funnel — full width */}
        <ChartCard
          title="Sales Pipeline"
          className="lg:col-span-2"
          tooltip={leadsEnabled
            ? 'Unified funnel from lead intake through booked deal. Shows cumulative progression and drop-off.'
            : 'Quote progression from Draft to Booked. Shows cumulative progression and drop-off.'
          }
        >
          {displayFunnel.length > 0 && displayFunnelMax > 0 ? (
            <div className="space-y-2.5 py-2">
              {displayFunnel.map((stage, i) => {
                const pct = displayFunnelMax > 0 ? (stage.count / displayFunnelMax) * 100 : 0;
                const colorIdx = leadsEnabled ? i : i + 3; // offset to match quote stages
                return (
                  <div key={stage.stage}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-midnight">{stage.stage}</span>
                      <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-full ${FUNNEL_COLORS[colorIdx] ?? FUNNEL_COLORS[FUNNEL_COLORS.length - 1]}`}>
                        {stage.count}
                      </span>
                    </div>
                    <div className="h-3 bg-mist rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${FUNNEL_BAR_COLORS[colorIdx] ?? FUNNEL_BAR_COLORS[FUNNEL_BAR_COLORS.length - 1]}`}
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {kpis.winRate > 0 && (
                <p className="text-xs text-slate text-center pt-1">
                  <span className="font-mono font-bold text-emerald-600">{kpis.winRate.toFixed(1)}%</span> win rate
                </p>
              )}
            </div>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No data available</p>
          )}
        </ChartCard>

        {/* Revenue Over Time */}
        <ChartCard title="Revenue Over Time" tooltip="Monthly won revenue from booked quotes in the selected period.">
          {revenueChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={revenueChartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradEmerald" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={{ stroke: '#E8E3EA' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => formatCurrency(v)}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} formatter={formatCurrency} />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#059669"
                  strokeWidth={2.5}
                  fill="url(#gradEmerald)"
                  dot={{ r: 3, fill: '#059669', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No data available</p>
          )}
        </ChartCard>

        {/* Lead Sources — only when leads enabled */}
        {showLeadSources ? (
          <ChartCard title="Lead Sources" tooltip="Where your leads come from — helps identify the best marketing channels.">
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={leadSourceDistribution}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {leadSourceDistribution.map((_, i) => (
                      <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => (
                      <ChartTooltip active={active} payload={payload as never} />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
                {leadSourceDistribution.map((entry, i) => (
                  <div key={entry.source} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
                    />
                    <span className="text-slate">{entry.source}</span>
                    <span className="font-mono font-semibold text-midnight">{entry.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>
        ) : (
          /* Outcome Reasons fills this slot when leads disabled */
          <OutcomeReasonsChart
            wonReasonDistribution={wonReasonDistribution}
            lostReasonDistribution={lostReasonDistribution}
            reasonView={reasonView}
            setReasonView={setReasonView}
          />
        )}

        {/* Outcome Reasons — when leads are enabled, show in its own slot */}
        {showLeadSources && (
          <OutcomeReasonsChart
            wonReasonDistribution={wonReasonDistribution}
            lostReasonDistribution={lostReasonDistribution}
            reasonView={reasonView}
            setReasonView={setReasonView}
          />
        )}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-4">
        <ChartCard title="Recent Activity" tooltip="Latest sales events across leads, quotes, and deals.">
          {displayActivity.length > 0 ? (
            <div className="divide-y divide-stone/60">
              {displayActivity.map(a => {
                const { icon: Icon, color } = EVENT_TYPE_ICONS[a.eventType] ?? EVENT_TYPE_ICONS.quote;
                return (
                  <button
                    key={`${a.id}-${a.eventType}`}
                    type="button"
                    onClick={noSubscription ? undefined : () => navigate(`/moves/${a.id}`)}
                    disabled={noSubscription}
                    className={`flex items-center gap-3 py-2.5 w-full text-left -mx-1 px-1
                               rounded-lg transition-colors group
                               ${noSubscription ? 'opacity-60 cursor-not-allowed' : 'hover:bg-mist/50'}`}
                  >
                    <div className={`shrink-0 ${color}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium text-midnight truncate transition-colors ${noSubscription ? '' : 'group-hover:text-plum'}`}>
                        {a.customerCompanyName}
                      </p>
                      <p className="text-xs text-slate truncate">
                        {a.event}
                      </p>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <span className="text-[11px] text-slate">{timeAgo(a.createdAt)}</span>
                      <span className="text-[10px] text-slate">{a.createdByName}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No recent activity</p>
          )}
        </ChartCard>
      </div>

      {/* Empty state */}
      {totalRecords === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BarChart3 size={56} className="text-stone mb-4" />
          <h2 className="font-display text-h3 text-midnight mb-2">No sales data yet</h2>
          <p className="text-small text-slate max-w-xs">
            {selectedUserId
              ? 'No data found for this estimator in the selected period.'
              : leadsEnabled
                ? 'Create your first lead or quote to start seeing analytics here.'
                : 'Create your first quote to start seeing analytics here.'}
          </p>
        </div>
      )}
    </>
  );
}

// ── Outcome Reasons Chart (extracted for reuse) ─────────────────────────────

function OutcomeReasonsChart({ wonReasonDistribution, lostReasonDistribution, reasonView, setReasonView }: {
  wonReasonDistribution: { reason: string; count: number }[];
  lostReasonDistribution: { reason: string; count: number }[];
  reasonView: 'won' | 'lost';
  setReasonView: (v: 'won' | 'lost') => void;
}) {
  return (
    <ChartCard title="Outcome Reasons" tooltip="Distribution of reasons for winning or losing quotes.">
      {wonReasonDistribution.length > 0 || lostReasonDistribution.length > 0 ? (
        <div className="flex flex-col items-center">
          <div className="flex gap-1 bg-mist rounded-lg p-0.5 mb-3 self-start">
            <button
              type="button"
              onClick={() => setReasonView('won')}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150
                ${reasonView === 'won'
                  ? 'bg-white text-emerald-700 shadow-brand-sm'
                  : 'text-slate hover:text-midnight'
                }`}
            >
              Won
            </button>
            <button
              type="button"
              onClick={() => setReasonView('lost')}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150
                ${reasonView === 'lost'
                  ? 'bg-white text-amber-700 shadow-brand-sm'
                  : 'text-slate hover:text-midnight'
                }`}
            >
              Lost
            </button>
          </div>
          {(() => {
            const reasonData = reasonView === 'won' ? wonReasonDistribution : lostReasonDistribution;
            if (reasonData.length === 0) {
              return (
                <p className="text-xs text-slate py-8">
                  No {reasonView === 'won' ? 'won' : 'lost'} quotes with reasons yet.
                </p>
              );
            }
            return (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={reasonData}
                      dataKey="count"
                      nameKey="reason"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      strokeWidth={0}
                    >
                      {reasonData.map((_, i) => (
                        <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => (
                        <ChartTooltip active={active} payload={payload as never} />
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
                  {reasonData.map((entry, i) => (
                    <div key={entry.reason} className="flex items-center gap-1.5 text-xs">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: REASON_COLORS[i % REASON_COLORS.length] }}
                      />
                      <span className="text-slate">{entry.reason}</span>
                      <span className="font-mono font-semibold text-midnight">{entry.count}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      ) : (
        <p className="text-[13px] text-slate text-center py-10">No data available</p>
      )}
    </ChartCard>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── OPERATIONS DASHBOARD ────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function OperationsDashboardContent({ orgId, period, selectedMoverId, onMoverChange, noSubscription, fleetEnabled, dispatchEnabled }: {
  orgId: string | null;
  period: JobsDashboardPeriod;
  selectedMoverId: string;
  onMoverChange: (id: string) => void;
  noSubscription: boolean;
  fleetEnabled: boolean;
  dispatchEnabled: boolean;
}) {
  const navigate = useNavigate();

  const { data, loading, error } = useJobsDashboard(
    orgId,
    period,
    selectedMoverId || undefined,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <Activity size={48} className="text-rose mb-4" />
        <h2 className="font-display text-h3 text-midnight mb-2">Something went wrong</h2>
        <p className="text-small text-slate mb-4">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { kpis, jobsOverTime, jobsByMover, methodDistribution, upcomingJobs, fleetStatus, availableMovers } = data;

  const chartMonthly = jobsOverTime.map(d => ({
    ...d,
    label: formatMonth(d.month),
  }));

  // Fleet status chart data
  const fleetChartData = fleetEnabled && fleetStatus.total > 0
    ? [
        { label: 'On Job', count: fleetStatus.onJob, color: '#059669' },
        { label: 'Available', count: fleetStatus.atWarehouse, color: '#2563EB' },
        { label: 'Maintenance', count: fleetStatus.maintenance, color: '#D97706' },
        { label: 'Inactive', count: fleetStatus.inactive, color: '#9E8FA3' },
      ].filter(d => d.count > 0)
    : [];

  return (
    <>
      {/* Mover filter */}
      {availableMovers.length > 1 && (
        <div className="mb-5 flex items-center">
          <CustomDropdown
            options={[
              { value: '', label: 'All movers' },
              ...availableMovers.map(u => ({ value: u.uid, label: u.name || 'Unknown' })),
            ]}
            value={selectedMoverId}
            onChange={onMoverChange}
            fullWidth={false}
            minWidth="160px"
            placeholder="All movers"
          />
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Active Jobs"
          value={String(kpis.activeJobs)}
          icon={<Activity size={18} />}
          color="plum"
          tooltip="Jobs currently scheduled or in progress."
        />
        {dispatchEnabled ? (
          <KpiCard
            label="Pending Assignment"
            value={String(kpis.pendingAssignment)}
            icon={<AlertTriangle size={18} />}
            color={kpis.pendingAssignment > 0 ? 'plum' : 'emerald'}
            tooltip="Booked jobs needing crew or truck assignment — dispatchers should address these."
          />
        ) : (
          <KpiCard
            label="Scheduled"
            value={String(kpis.scheduledCount)}
            icon={<Calendar size={18} />}
            color="emerald"
            tooltip="Jobs scheduled but not yet started."
          />
        )}
        {fleetEnabled ? (
          <KpiCard
            label="Fleet Utilization"
            value={fleetStatus.total > 0 ? `${kpis.fleetUtilization.toFixed(0)}%` : '—'}
            icon={<Truck size={18} />}
            color="plum"
            tooltip="Percentage of active trucks currently assigned to a job."
          />
        ) : (
          <KpiCard
            label="Avg Crew Size"
            value={kpis.avgCrewSize > 0 ? String(kpis.avgCrewSize) : '—'}
            icon={<Users size={18} />}
            color="plum"
            tooltip="Average crew size across all jobs with logistics data."
          />
        )}
        <KpiCard
          label="Completion Rate"
          value={`${kpis.completionRate.toFixed(0)}%`}
          icon={<Target size={18} />}
          color="emerald"
          tooltip="Completed ÷ (Total − Cancelled) — operational execution quality."
        />
      </div>

      {/* Upcoming Jobs Timeline — full width */}
      <div className="mb-6">
        <ChartCard title="Upcoming Jobs" tooltip="Next 10 upcoming jobs sorted by move date. Color-coded assignment status shows readiness.">
          {upcomingJobs.length > 0 ? (
            <div className="divide-y divide-stone/60">
              {upcomingJobs.map(j => {
                const badge = ASSIGNMENT_BADGE[j.assignmentStatus] ?? ASSIGNMENT_BADGE.unassigned;
                return (
                  <button
                    key={j.id}
                    type="button"
                    onClick={noSubscription ? undefined : () => navigate(`/moves/${j.id}`)}
                    disabled={noSubscription}
                    className={`flex items-center gap-3 py-2.5 w-full text-left -mx-1 px-1
                               rounded-lg transition-colors group
                               ${noSubscription ? 'opacity-60 cursor-not-allowed' : 'hover:bg-mist/50'}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium text-midnight truncate transition-colors ${noSubscription ? '' : 'group-hover:text-plum'}`}>
                        {j.customerCompanyName}
                      </p>
                      <p className="text-xs text-slate">
                        {j.crewLeadName}
                        {j.moveDate && <> &middot; {formatDate(j.moveDate)}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {j.crewSize > 0 && (
                        <span className="text-[10px] text-slate font-medium flex items-center gap-0.5">
                          <Users size={10} /> {j.crewSize}
                        </span>
                      )}
                      {j.truckCount > 0 && (
                        <span className="text-[10px] text-slate font-medium flex items-center gap-0.5">
                          <Truck size={10} /> {j.truckCount}
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No upcoming jobs</p>
          )}
        </ChartCard>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Jobs Over Time */}
        <ChartCard title="Jobs Over Time" className="lg:col-span-2" tooltip="Number of jobs per month (by move date or creation date).">
          {chartMonthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartMonthly} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={{ stroke: '#E8E3EA' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Jobs"
                  stroke="#2563EB"
                  strokeWidth={2.5}
                  fill="url(#gradBlue)"
                  dot={{ r: 3, fill: '#2563EB', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No data available</p>
          )}
        </ChartCard>

        {/* Move Method Distribution */}
        <ChartCard title="Method Distribution" tooltip="Distribution of jobs by moving method (Shuttle vs Load & Go).">
          {methodDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={methodDistribution} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" />
                <XAxis
                  dataKey="method"
                  tick={{ fontSize: 11, fill: 'var(--color-text-primary)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Bar dataKey="count" name="Jobs" fill="#2563EB" radius={[6, 6, 0, 0]} barSize={48} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No data available</p>
          )}
        </ChartCard>

        {/* Fleet Status — only when fleet is enabled */}
        {fleetEnabled && fleetChartData.length > 0 && (
          <ChartCard title="Fleet Status" tooltip="Current truck status: on a job, available at warehouse, in maintenance, or inactive.">
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={fleetChartData}
                    dataKey="count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {fleetChartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => (
                      <ChartTooltip active={active} payload={payload as never} />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-1">
                {fleetChartData.map(entry => (
                  <div key={entry.label} className="flex items-center gap-1.5 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-slate">{entry.label}</span>
                    <span className="font-mono font-semibold text-midnight">{entry.count}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate mt-2">
                {fleetStatus.total} total trucks
              </p>
            </div>
          </ChartCard>
        )}
      </div>

      {/* Crew Workload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <ChartCard title="Crew Workload" tooltip="Number of jobs per crew member in the selected period. Helps balance workload across the team.">
          {jobsByMover.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(160, jobsByMover.length * 44)}>
              <BarChart
                data={jobsByMover}
                layout="vertical"
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E3EA" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#9E8FA3' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: 'var(--color-text-primary)' }}
                  axisLine={false}
                  tickLine={false}
                  width={100}
                />
                <Tooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip active={active} payload={payload as never} label={label as string} />
                  )}
                />
                <Bar dataKey="count" name="Jobs" fill="#820263" radius={[0, 6, 6, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[13px] text-slate text-center py-10">No data available</p>
          )}
        </ChartCard>
      </div>

      {/* Empty state */}
      {kpis.totalJobs === 0 && kpis.pendingAssignment === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Settings size={56} className="text-stone mb-4" />
          <h2 className="font-display text-h3 text-midnight mb-2">No operations data yet</h2>
          <p className="text-small text-slate max-w-xs">
            {selectedMoverId
              ? 'No jobs found for this mover in the selected period.'
              : 'Jobs will appear here once quotes are won and a Move Lead is assigned.'}
          </p>
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── SHARED SUB-COMPONENTS ───────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-slate/50 hover:text-plum transition-colors"
        aria-label="More info"
      >
        <Info size={14} />
      </button>
      {open && (
        <span className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-1.5
                         bg-midnight text-white text-[11px] leading-snug rounded-lg
                         px-2.5 py-1.5 w-48 shadow-lg pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

function KpiCard({ label, value, icon, color, tooltip }: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: 'plum' | 'emerald';
  tooltip?: string;
}) {
  const bg = color === 'plum' ? 'bg-plum/8' : 'bg-emerald-50';
  const iconColor = color === 'plum' ? 'text-plum' : 'text-emerald-600';

  return (
    <div className="bg-white border border-stone rounded-xl p-3.5 shadow-brand-sm
                    hover:shadow-brand-md hover:border-plum/30 transition-all">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center ${iconColor}`}>
          {icon}
        </div>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <p className="font-mono text-xl font-bold text-midnight leading-tight">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate mt-1">{label}</p>
    </div>
  );
}


function ChartCard({ title, children, className = '', tooltip }: {
  title: string;
  children: React.ReactNode;
  className?: string;
  tooltip?: string;
}) {
  return (
    <div className={`bg-white border border-stone rounded-xl p-4 shadow-brand-sm ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-display text-sm font-semibold text-midnight">{title}</h3>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      {children}
    </div>
  );
}
